import { aircraftDetailQuery } from "@common/api/aircraft"
import { atlPagesInfiniteQuery } from "@common/api/atl"
import { currentUserQuery } from "@common/api/users"
import { WO_PAGES } from "@common/flags"
import { hasPermission } from "@common/lib/permissions"
import {
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { requireFeatureFlag } from "@/lib/require-feature-flag"
import { techLogPagesParams } from "../-lib/tech-log"
import { PageCreateForm } from "./-components/form/create-form"
import { PagePane } from "./-components/page-pane"
import { CLOSED_SCAN } from "./-components/source"

export const Route = createFileRoute(
  "/_layout/aircraft_/$aircraftId/tech-log/new",
)({
  beforeLoad: async ({ context: { queryClient }, params: { aircraftId } }) => {
    await requireFeatureFlag(queryClient, WO_PAGES)
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "atl_page:create")) {
      throw redirect({
        to: "/aircraft/$aircraftId",
        params: { aircraftId },
        search: { tab: "tech-log" },
      })
    }
  },
  loader: ({ context: { queryClient }, params: { aircraftId } }) =>
    queryClient.ensureInfiniteQueryData(
      atlPagesInfiniteQuery(techLogPagesParams(aircraftId, {})),
    ),
  component: NewTechLogPagePane,
})

function NewTechLogPagePane(): React.ReactElement {
  const { aircraftId } = Route.useParams()
  const { data: aircraft } = useSuspenseQuery(
    aircraftDetailQuery({ id: aircraftId }),
  )
  const { data } = useSuspenseInfiniteQuery(
    atlPagesInfiniteQuery(techLogPagesParams(aircraftId, {})),
  )
  const latest = data.pages[0]?.data[0]
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const close = () =>
    navigate({
      to: "/aircraft/$aircraftId",
      params: { aircraftId },
      search: (previous) => ({
        ...previous,
        tab: "tech-log" as const,
        ...CLOSED_SCAN,
        wide: undefined,
      }),
    })

  return (
    <PagePane
      title="New tech log"
      page={undefined}
      stepper={undefined}
      onClose={close}
    >
      <PageCreateForm
        aircraftId={aircraftId}
        latest={latest}
        onCreate={(created) => {
          showToast(
            `Created ${created.log_number}`,
            aircraft.registration_no,
            "success",
          )
          void navigate({
            to: "/aircraft/$aircraftId/tech-log/$pageId",
            params: { aircraftId, pageId: created.id },
            search: true,
            replace: true,
          })
        }}
        onCancel={close}
      />
    </PagePane>
  )
}
